import { useFieldArray, type UseFormReturn } from 'react-hook-form'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { emptyContact, type CommitmentLetterFormInput } from './schema'
import { FieldError } from './FieldError'

type ContactListName = 'sampleContacts' | 'confirmationContacts'

interface ContactListProps {
  form: UseFormReturn<CommitmentLetterFormInput>
  name: ContactListName
  title: string
  hint: string
}

function ContactList({ form, name, title, hint }: ContactListProps) {
  const { control, register, formState } = form
  const { fields, append, remove } = useFieldArray({ control, name })
  const listErrors = formState.errors[name]

  return (
    <div className="flex flex-col gap-2">
      <div>
        <Label>{title}</Label>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      {fields.map((field, index) => (
        <div key={field.id} className="flex items-start gap-2">
          <div className="flex flex-1 flex-col gap-1">
            <Input
              placeholder="Name"
              className="h-9"
              {...register(`${name}.${index}.name` as const)}
            />
            <FieldError message={listErrors?.[index]?.name?.message} />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <Input
              placeholder="email@michaels.com"
              type="email"
              className="h-9"
              {...register(`${name}.${index}.email` as const)}
            />
            <FieldError message={listErrors?.[index]?.email?.message} />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            aria-label="Remove contact"
            disabled={fields.length === 1}
            onClick={() => remove(index)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <FieldError message={listErrors?.root?.message ?? listErrors?.message} />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit gap-1"
        onClick={() => append(emptyContact())}
      >
        <Plus className="h-3.5 w-3.5" />
        Add contact
      </Button>
    </div>
  )
}

export function StepContacts({
  form,
}: {
  form: UseFormReturn<CommitmentLetterFormInput>
}) {
  return (
    <div className="flex flex-col gap-6">
      <ContactList
        form={form}
        name="sampleContacts"
        title="Samples contacts"
        hint='"Please contact … if you have any questions" (POG samples section).'
      />
      <ContactList
        form={form}
        name="confirmationContacts"
        title="Confirmation contacts"
        hint='"Please sign below in confirmation and email confirmation to … by the due date."'
      />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="cl-signers">Signed by</Label>
        <Input
          id="cl-signers"
          className="h-9"
          placeholder="Defaults to the confirmation contacts (comma separated)"
          {...form.register('signerNames')}
        />
        <FieldError message={form.formState.errors.signerNames?.message} />
      </div>
    </div>
  )
}
